import { useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { MenuItem } from "@/types/domain";
import { FavouritesSubMenu } from "./FavouritesSubMenu";
import { PipelinesSubMenu } from "./PipelinesSubMenu";
import { flyoutRowClass } from "./flyoutStyles";
import { useMenuNavigation } from "./useMenuNavigation";

type NavFlyoutProps = {
  item: MenuItem;
  open: boolean;
  onClose: () => void;
  onReturnFocus: () => void;
}

const ITEM_SELECTOR = "[data-flyout-item]";

const getAnchorLeft = () => {
  if (window.innerWidth < 1024) return 0;
  return document.querySelector(".layout-side-nav")?.getBoundingClientRect().right ?? 0;
}

export const NavFlyout = ({ item, open, onClose, onReturnFocus }: NavFlyoutProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const items = () => Array.from(contentRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);

  const focusItem = (target: HTMLElement | undefined) => target?.focus();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const all = items();
    const index = all.indexOf(document.activeElement as HTMLElement);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusItem(all[index < 0 ? 0 : (index + 1) % all.length]);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusItem(all[index <= 0 ? all.length - 1 : index - 1]);
        break;
      case "Home":
        e.preventDefault();
        focusItem(all[0]);
        break;
      case "End":
        e.preventDefault();
        focusItem(all[all.length - 1]);
        break;
      case "ArrowLeft":
        e.preventDefault();
        onClose();
        break;
    }
  }

  const handleOpenAutoFocus = (e: Event) => {
    e.preventDefault();
    const first = items()[0];
    (first ?? contentRef.current)?.focus();
  }

  const handleCloseAutoFocus = (e: Event) => {
    e.preventDefault();
    onReturnFocus();
  }

  const handleInteractOutside = (e: Event) => {
    const target = e.target as Element | null;
    if (target?.closest(`[data-nav-flyout-trigger="${CSS.escape(item.id)}"]`)) e.preventDefault();
  }

  const lastAnchorLeft = useRef(0);
  if (open) lastAnchorLeft.current = getAnchorLeft();
  const anchorLeft = lastAnchorLeft.current;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }} modal={false}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          ref={contentRef}
          tabIndex={-1}
          aria-describedby={undefined}
          data-testid="nav-flyout"
          onKeyDown={handleKeyDown}
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          onInteractOutside={handleInteractOutside}
          className="fixed z-50 flex flex-col bg-white text-slate-900 shadow-2xl outline-none w-[300px] max-w-[calc(100vw-48px)] max-lg:w-screen max-lg:max-w-none settings-panel-motion"
          style={{ top: "var(--layout-top-bar-height)", bottom: 0, left: anchorLeft }}
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <DialogPrimitive.Title className="text-base font-semibold">{item.label}</DialogPrimitive.Title>
            <DialogPrimitive.Close className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label={`Close ${item.label}`}>
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-slate-200 px-2 py-2">
            <ul>
              {item.type === "favourites" && <FavouritesSubMenu onActivated={onClose} />}
              {item.type === "pipelines" && <PipelinesSubMenu onActivated={onClose} />}
              {item.type === "dropdown" && <FlyoutChildren items={item.children} onActivated={onClose} />}
            </ul>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const FlyoutChildren = ({ items, onActivated }: { items: MenuItem[] | null | undefined; onActivated: () => void }) => (
  <>
    {(items ?? []).filter((c) => c.isVisible).map((child) =>
      child.type === "dropdown" ? (
        <li key={child.id} role="presentation" data-testid={child.testId}>
          <div className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{child.label}</div>
          <ul className="pl-2">
            <FlyoutChildren items={child.children} onActivated={onActivated} />
          </ul>
        </li>
      ) : (
        <li key={child.id}>
          <FlyoutLeaf leaf={child} onActivated={onActivated} />
        </li>
      ),
    )}
  </>
);

const FlyoutLeaf = ({ leaf, onActivated }: { leaf: MenuItem; onActivated: () => void }) => {
  const { handlePopupClick, handleLinkClick } = useMenuNavigation();
  const className = flyoutRowClass(leaf.isSelected);
  const content = (
    <>
      <span className="truncate">{leaf.label}</span>
      {leaf.hint && <span className={leaf.isSelected ? "text-xs text-white/80" : "text-xs text-slate-400"}>{leaf.hint}</span>}
    </>
  );

  if (leaf.type === "popup") {
    return (
      <button type="button" className={className} data-flyout-item data-testid={leaf.testId} onClick={(e) => { handlePopupClick(e, leaf); onActivated(); }}>
        {content}
      </button>
    );
  }
  return (
    <a href={leaf.url ?? "#"} className={className} data-flyout-item data-testid={leaf.testId} onClick={(e) => { handleLinkClick(e, leaf); onActivated(); }}>
      {content}
    </a>
  );
}
